'use client';

import useToast from '@/hooks/shared/useToast';

import { copyTextToClipboard } from '@/utils/copyTextToClipboard';

import {
  AI_SERVICES,
  AiServiceAction,
} from '@/components/apps/ui/ai/AiServiceAction';
import { IconChainSharp, IconLinkedIn } from '@/components/apps/ui/icon';

import {
  AiService,
  buildAiAskUrl,
  buildIndustrialCasePrompt,
  buildLinkedInShareUrl,
} from './industrialCaseShare';

type Labels = {
  aiServices: Record<AiService, string>;
  title: string;
  description: string;
  copyLink: string;
  shareLinkedIn: string;
  promptCopied: string;
  linkCopied: string;
  copyFailed: string;
  promptIntroduction: string;
  promptBrandStatement: string;
  promptQuestion: string;
};

type Props = {
  labels: Labels;
  isInteractive?: boolean;
  /** Reports which AI service was opened. Absent in the admin preview. */
  onAskAi?: (service: AiService) => void;
};

/**
 * ### IndustrialCaseShareActions
 *
 * Presents AI and social sharing actions for a published case. Preview mode
 * retains the public layout while suppressing clipboard and window effects.
 *
 * @param props Localized labels and interaction mode
 * @returns The case sharing section
 */
export function IndustrialCaseShareActions({
  labels,
  isInteractive = true,
  onAskAi,
}: Props) {
  const { showToast } = useToast();

  const createPrompt = () =>
    buildIndustrialCasePrompt({
      sourceUrl: window.location.href,
      introduction: labels.promptIntroduction,
      brandStatement: labels.promptBrandStatement,
      question: labels.promptQuestion,
    });

  const openAiService = async (service: AiService) => {
    onAskAi?.(service);
    const prompt = createPrompt();
    const copied = await copyTextToClipboard(prompt);
    showToast({
      state: copied ? 'positive' : 'negative',
      message: copied ? labels.promptCopied : labels.copyFailed,
    });
    window.open(
      buildAiAskUrl(service, prompt),
      '_blank',
      'noopener,noreferrer',
    );
  };

  const copyLink = async () => {
    const copied = await copyTextToClipboard(window.location.href);
    showToast({
      state: copied ? 'positive' : 'negative',
      message: copied ? labels.linkCopied : labels.copyFailed,
    });
  };

  const shareLinkedIn = () => {
    window.open(
      buildLinkedInShareUrl(window.location.href),
      '_blank',
      'noopener,noreferrer',
    );
  };

  return (
    <section className="mb-12 max-w-180 py-10 md:mb-16 md:py-12">
      <h2 className="text-[clamp(27px,3vw,38px)] font-semibold leading-[1.18] tracking-tight text-apps-brandGrayBlack text-balance">
        {labels.title}
      </h2>
      <p className="mt-4 max-w-155 text-[16px] font-medium leading-[1.7] tracking-[-0.01em] text-[#4a4a46]">
        {labels.description}
      </p>

      <div className="mt-8 grid gap-2 sm:grid-cols-2">
        {AI_SERVICES.map((service) => (
          <AiServiceAction
            key={service.id}
            service={service}
            label={labels.aiServices[service.id]}
            isInteractive={isInteractive}
            onClick={() => openAiService(service.id)}
          />
        ))}
      </div>

      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        <button
          className="flex min-h-12 touch-manipulation cursor-pointer items-center justify-between bg-[#efefec] px-4 text-left text-sm font-semibold text-apps-brandGrayBlack transition-[background-color,transform] duration-200 ease-out hover:bg-[#e4e4e0] active:scale-[0.98] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-apps-brandGrayBlack motion-reduce:transform-none motion-reduce:transition-none"
          onClick={isInteractive ? copyLink : undefined}
          aria-disabled={!isInteractive}
          tabIndex={isInteractive ? 0 : -1}
          type="button"
        >
          <span>{labels.copyLink}</span>
          <IconChainSharp size={16} aria-hidden="true" />
        </button>
        <button
          className="flex min-h-12 touch-manipulation cursor-pointer items-center justify-between bg-[#efefec] px-4 text-left text-sm font-semibold text-apps-brandGrayBlack transition-[background-color,transform] duration-200 ease-out hover:bg-[#e4e4e0] active:scale-[0.98] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-apps-brandGrayBlack motion-reduce:transform-none motion-reduce:transition-none"
          onClick={isInteractive ? shareLinkedIn : undefined}
          aria-disabled={!isInteractive}
          tabIndex={isInteractive ? 0 : -1}
          type="button"
        >
          <span>{labels.shareLinkedIn}</span>
          <IconLinkedIn size={16} aria-hidden="true" />
        </button>
      </div>
    </section>
  );
}
